import React, { useState } from 'react';
import { ShoppingCart, Star, Package, Plus, Minus, Heart, Filter, ArrowLeft, Brain, Menu, X, Sparkles } from 'lucide-react';
import ComingSoon from './ComingSoon';
import SaucePersonalizer from './SaucePersonalizer';
import CartNotification from './CartNotification';
import { sauceUtils, MASTER_SAUCES, SAMPLER_PACK, SAUCE_CATEGORIES } from '../data/sauces';
import { functionalIngredientUtils } from '../data/functionalIngredients';

interface CartItem {
  sauceId: string;
  quantity: number;
  selectedIngredients?: string[];
  totalPrice: number;
}

interface CartNotificationState {
  isVisible: boolean;
  productName: string;
  isPersonalized: boolean;
  ingredientCount: number;
  totalPrice: number;
}

export default function Shop() {
  const [cart, setCart] = useState<Record<string, CartItem>>({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [personalizerOpen, setPersonalizerOpen] = useState(false);
  const [selectedSauce, setSelectedSauce] = useState<any>(null);
  const [cartNotification, setCartNotification] = useState<CartNotificationState>({
    isVisible: false,
    productName: '',
    isPersonalized: false,
    ingredientCount: 0,
    totalPrice: 0
  });

  // Get sauce data from centralized source
  const sauces = sauceUtils.getAllSauces();
  const categories = SAUCE_CATEGORIES;
  const samplerPack = SAMPLER_PACK;

  // Show checkout coming soon page when user tries to checkout
  if (showCheckout) {
    return (
      <ComingSoon
        title="Checkout Coming Soon"
        subtitle="Payment processing is almost ready"
        description="We're finalizing our secure payment system and inventory management. Your cart will be saved for when we launch!"
        onBack={() => setShowCheckout(false)}
        showNotifyButton={true}
        icon={ShoppingCart}
        estimatedLaunch="March 2025"
      />
    );
  }

  const showCartNotification = (productName: string, isPersonalized = false, ingredientCount = 0, totalPrice = 0) => {
    setCartNotification({
      isVisible: true,
      productName,
      isPersonalized,
      ingredientCount,
      totalPrice
    });
  };

  const hideCartNotification = () => {
    setCartNotification(prev => ({ ...prev, isVisible: false }));
  };

  const addToCart = (productId: string) => {
    setCart(prev => {
      const existingItem = prev[productId];
      if (existingItem) {
        const newItem = {
          ...existingItem,
          quantity: existingItem.quantity + 1,
          totalPrice: existingItem.totalPrice + (existingItem.totalPrice / existingItem.quantity)
        };
        
        // Show notification
        const sauce = sauceUtils.getSauceById(productId);
        const productName = productId === samplerPack.id ? samplerPack.name : (sauce?.name || 'Product');
        showCartNotification(productName, false, 0, newItem.totalPrice / newItem.quantity);
        
        return {
          ...prev,
          [productId]: newItem
        };
      } else {
        const sauce = sauceUtils.getSauceById(productId);
        const price = productId === samplerPack.id ? samplerPack.price : (sauce?.price || 0);
        const productName = productId === samplerPack.id ? samplerPack.name : (sauce?.name || 'Product');
        
        // Show notification
        showCartNotification(productName, false, 0, price);
        
        return {
          ...prev,
          [productId]: {
            sauceId: productId,
            quantity: 1,
            totalPrice: price
          }
        };
      }
    });
  };

  const addPersonalizedToCart = (sauceId: string, selectedIngredients: string[], totalPrice: number) => {
    const cartKey = `${sauceId}-${selectedIngredients.sort().join('-')}`;
    setCart(prev => {
      const existingItem = prev[cartKey];
      if (existingItem) {
        const newItem = {
          ...existingItem,
          quantity: existingItem.quantity + 1,
          totalPrice: existingItem.totalPrice + totalPrice
        };
        
        // Show notification
        const sauce = sauceUtils.getSauceById(sauceId);
        showCartNotification(
          sauce?.name || 'Personalized Sauce', 
          true, 
          selectedIngredients.length, 
          totalPrice
        );
        
        return {
          ...prev,
          [cartKey]: newItem
        };
      } else {
        // Show notification
        const sauce = sauceUtils.getSauceById(sauceId);
        showCartNotification(
          sauce?.name || 'Personalized Sauce', 
          true, 
          selectedIngredients.length, 
          totalPrice
        );
        
        return {
          ...prev,
          [cartKey]: {
            sauceId,
            quantity: 1,
            selectedIngredients,
            totalPrice
          }
        };
      }
    });
  };

  const removeFromCart = (productId: string) => {
    setCart(prev => {
      const newCart = { ...prev };
      const existingItem = newCart[productId];
      if (existingItem && existingItem.quantity > 1) {
        newCart[productId] = {
          ...existingItem,
          quantity: existingItem.quantity - 1,
          totalPrice: existingItem.totalPrice - (existingItem.totalPrice / existingItem.quantity)
        };
      } else {
        delete newCart[productId];
      }
      return newCart;
    });
  };

  const getCartTotal = () => {
    return Object.values(cart).reduce((total, item) => total + item.totalPrice, 0);
  };

  const getCartItemCount = () => {
    return Object.values(cart).reduce((sum, item) => sum + item.quantity, 0);
  };

  const filteredSauces = sauceUtils.getSaucesByCategory(selectedCategory);

  const getHeatColor = (heat: string) => {
    switch (heat) {
      case 'Extreme': return 'from-red-600 to-red-800';
      case 'Hot': return 'from-orange-500 to-red-600';
      case 'Medium': return 'from-yellow-500 to-orange-500';
      case 'Mild': return 'from-green-500 to-yellow-500';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  const openPersonalizer = (sauce: any) => {
    setSelectedSauce(sauce);
    setPersonalizerOpen(true);
  };

  return (
    <div className="min-h-screen py-16 sm:py-20 relative overflow-hidden">
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-800 to-pink-900"></div>
      <div className="absolute inset-0 bg-gradient-to-tr from-pink-500/20 via-purple-600/30 to-blue-600/20 animate-pulse"></div>
      
      {/* Floating elements */}
      <div className="absolute top-20 left-10 w-2 h-2 sm:w-3 sm:h-3 bg-pink-500 rounded-full animate-pulse"></div>
      <div className="absolute top-40 right-20 w-3 h-3 sm:w-4 sm:h-4 bg-purple-500 rounded-full animate-bounce"></div>
      <div className="absolute bottom-40 left-20 w-1 h-1 sm:w-2 sm:h-2 bg-pink-400 rounded-full animate-ping"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-4 sm:mb-6">
            <img src="/image.png" alt="UNDRESSING™ Sauce Logo" className="h-8 w-8 sm:h-12 sm:w-12" />
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black">
              <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                UNDRESSING™ Sauce
              </span>
              <span className="text-white"> Shop</span>
            </h1>
          </div>
          <p className="text-lg sm:text-xl text-gray-300">Global fusion flavors, on the go - Premium sauce pouches for every adventure</p>
          
          {/* Personalization Feature Highlight */}
          <div className="mt-6 inline-flex items-center space-x-2 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 border border-cyan-500/30 rounded-full px-4 py-2">
            <Sparkles className="h-4 w-4 text-cyan-400" />
            <span className="text-cyan-300 font-medium text-sm">NEW: Personalize with functional ingredients</span>
          </div>
        </div>

        {/* Mobile Cart Summary */}
        {getCartItemCount() > 0 && (
          <div className="bg-gradient-to-r from-pink-500/20 to-purple-600/20 border border-pink-500/30 rounded-xl sm:rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row items-center justify-between space-y-3 sm:space-y-0">
              <div className="flex items-center space-x-3 sm:space-x-4">
                <ShoppingCart className="h-5 w-5 sm:h-6 sm:w-6 text-pink-400" />
                <span className="text-white font-semibold text-sm sm:text-base">
                  {getCartItemCount()} item{getCartItemCount() !== 1 ? 's' : ''} in cart
                </span>
              </div>
              <div className="text-center sm:text-right">
                <div className="text-xl sm:text-2xl font-bold text-white">S${getCartTotal().toFixed(2)}</div>
                <button 
                  onClick={() => setShowCheckout(true)}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-4 sm:px-6 py-2 rounded-full font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 mt-2 text-sm sm:text-base"
                >
                  Checkout
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Sampler Pack Feature - Updated with new description */}
        <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-pink-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-8 sm:mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-center">
            <div>
              <div className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-3 py-1 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-bold inline-block mb-3 sm:mb-4">
                BESTSELLER
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3 sm:mb-4">{samplerPack.name}</h2>
              <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">{samplerPack.description}</p>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 sm:gap-4 mb-4 sm:mb-6">
                {samplerPack.includes.map((sauce, index) => (
                  <div key={index} className="flex items-center space-x-2">
                    <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-pink-400 rounded-full"></div>
                    <span className="text-gray-300 text-xs sm:text-sm">{sauce}</span>
                  </div>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-6">
                <div className="text-2xl sm:text-3xl font-bold text-white">S${samplerPack.price.toFixed(2)}</div>
                <div className="text-gray-400 line-through text-lg sm:text-xl">S${samplerPack.originalPrice.toFixed(2)}</div>
                <div className="bg-green-500/20 text-green-400 px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm font-semibold">
                  Save S${samplerPack.savings.toFixed(2)}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
                {cart[samplerPack.id] && (
                  <div className="flex items-center justify-center space-x-3 bg-black/50 rounded-lg px-4 py-2">
                    <button
                      onClick={() => removeFromCart(samplerPack.id)}
                      className="text-pink-400 hover:text-pink-300"
                    >
                      <Minus className="h-4 w-4" />
                    </button>
                    <span className="text-white font-semibold">{cart[samplerPack.id].quantity}</span>
                    <button
                      onClick={() => addToCart(samplerPack.id)}
                      className="text-pink-400 hover:text-pink-300"
                    >
                      <Plus className="h-4 w-4" />
                    </button>
                  </div>
                )}
                <button
                  onClick={() => addToCart(samplerPack.id)}
                  className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 sm:px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 flex items-center justify-center space-x-2 text-sm sm:text-base"
                >
                  <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5" />
                  <span>Add Sampler Pack</span>
                </button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-gradient-to-br from-pink-500/20 to-purple-600/20 rounded-xl sm:rounded-2xl p-4 sm:p-8">
                <img 
                  src={samplerPack.image} 
                  alt={samplerPack.name}
                  className="w-full h-48 sm:h-64 object-cover rounded-lg sm:rounded-xl"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Category Filter */}
        <div className="mb-6 sm:mb-8">
          <div className="flex items-center justify-between mb-3 sm:mb-4">
            <div className="flex items-center space-x-2 text-gray-400">
              <Filter className="h-4 w-4 sm:h-5 sm:w-5" />
              <span className="text-sm sm:text-base">Filter:</span>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="sm:hidden flex items-center space-x-2 bg-gray-800 text-gray-300 px-3 py-2 rounded-lg"
            >
              <span className="text-sm">Categories</span>
              {showFilters ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </button>
          </div>
          
          {/* Desktop Filter */}
          <div className="hidden sm:flex flex-wrap gap-3 lg:gap-4">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`px-3 lg:px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  selectedCategory === category.id
                    ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {category.name} ({category.count})
              </button>
            ))}
          </div>

          {/* Mobile Filter Dropdown */}
          {showFilters && (
            <div className="sm:hidden bg-gray-900/90 backdrop-blur-sm border border-gray-700 rounded-lg p-4 space-y-2">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setShowFilters(false);
                  }}
                  className={`block w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category.id
                      ? 'bg-gradient-to-r from-pink-500 to-purple-600 text-white'
                      : 'text-gray-300 hover:bg-gray-800'
                  }`}
                >
                  {category.name} ({category.count})
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Individual Sauces Grid - Updated with new sauce data */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
          {filteredSauces.map((sauce) => (
            <div key={sauce.id} className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-pink-500/20 rounded-xl sm:rounded-2xl p-4 sm:p-6 hover:border-pink-400/40 transition-all duration-300 group">
              <div className="relative mb-3 sm:mb-4">
                <img 
                  src={sauce.image} 
                  alt={sauce.name}
                  className="w-full h-36 sm:h-48 object-cover rounded-lg sm:rounded-xl group-hover:scale-105 transition-transform duration-300"
                />
                <button className="absolute top-2 sm:top-3 right-2 sm:right-3 p-1.5 sm:p-2 bg-black/50 rounded-full text-gray-400 hover:text-pink-400 transition-colors">
                  <Heart className="h-3 w-3 sm:h-4 sm:w-4" />
                </button>
                <div className={`absolute bottom-2 sm:bottom-3 left-2 sm:left-3 bg-gradient-to-r ${getHeatColor(sauce.heat)} px-2 sm:px-3 py-1 rounded-full text-white text-xs font-bold`}>
                  {sauce.heat}
                </div>
              </div>

              <h3 className="text-lg sm:text-xl font-bold text-white mb-2">{sauce.name}</h3>
              <p className="text-gray-300 text-xs sm:text-sm mb-2 italic">"{sauce.description}"</p>
              <p className="text-gray-400 text-xs mb-3 sm:mb-4">{sauce.flavorProfile}</p>

              <div className="flex items-center space-x-2 mb-3 sm:mb-4">
                <div className="flex items-center space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`h-3 w-3 sm:h-4 sm:w-4 ${i < Math.floor(sauce.rating) ? 'text-yellow-400 fill-current' : 'text-gray-600'}`} 
                    />
                  ))}
                </div>
                <span className="text-gray-400 text-xs sm:text-sm">({sauce.reviews})</span>
              </div>

              <div className="flex items-center justify-between mb-4">
                <div className="text-xl sm:text-2xl font-bold text-white">S${sauce.price.toFixed(2)}</div>
              </div>

              {/* Personalization Options */}
              <div className="space-y-3 mb-4">
                <button
                  onClick={() => openPersonalizer(sauce)}
                  className="w-full bg-gradient-to-r from-cyan-500 to-purple-600 text-white py-2 rounded-lg font-semibold hover:shadow-lg hover:shadow-cyan-500/25 transition-all duration-300 flex items-center justify-center space-x-2 text-sm"
                >
                  <Sparkles className="h-4 w-4" />
                  <span>Personalize + Add</span>
                </button>
                
                <div className="flex items-center space-x-2">
                  {cart[sauce.id] && (
                    <div className="flex items-center space-x-2 bg-black/50 rounded-lg px-2 sm:px-3 py-1">
                      <button
                        onClick={() => removeFromCart(sauce.id)}
                        className="text-pink-400 hover:text-pink-300"
                      >
                        <Minus className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                      <span className="text-white font-semibold text-sm">{cart[sauce.id].quantity}</span>
                      <button
                        onClick={() => addToCart(sauce.id)}
                        className="text-pink-400 hover:text-pink-300"
                      >
                        <Plus className="h-3 w-3 sm:h-4 sm:w-4" />
                      </button>
                    </div>
                  )}
                  <button
                    onClick={() => addToCart(sauce.id)}
                    className="flex-1 bg-gradient-to-r from-pink-500 to-purple-600 text-white py-2 rounded-lg font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 flex items-center justify-center space-x-2 text-sm"
                  >
                    <ShoppingCart className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Quick Add</span>
                  </button>
                </div>
              </div>

              {/* Pairing Suggestions */}
              <div className="bg-black/30 rounded-lg p-3 mt-3">
                <h4 className="text-cyan-400 font-semibold text-xs mb-1">Perfect with:</h4>
                <p className="text-gray-300 text-xs">{sauce.pairings}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12 sm:mt-16">
          <div className="bg-gradient-to-br from-gray-900/80 to-black/80 backdrop-blur-sm border border-pink-500/20 rounded-xl sm:rounded-2xl p-6 sm:p-8">
            <Package className="h-12 w-12 sm:h-16 sm:w-16 text-pink-400 mx-auto mb-3 sm:mb-4" />
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3 sm:mb-4">Can't Decide?</h3>
            <p className="text-gray-300 mb-4 sm:mb-6 text-sm sm:text-base">
              Take our DrizzID™ quiz to get personalized sauce recommendations based on your unique flavor profile
            </p>
            <button className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-6 sm:px-8 py-3 rounded-full font-semibold hover:shadow-lg hover:shadow-pink-500/25 transition-all duration-300 flex items-center space-x-2 mx-auto text-sm sm:text-base">
              <Brain className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Take DrizzID™ Quiz</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sauce Personalizer Modal */}
      {selectedSauce && (
        <SaucePersonalizer
          sauce={selectedSauce}
          isOpen={personalizerOpen}
          onClose={() => {
            setPersonalizerOpen(false);
            setSelectedSauce(null);
          }}
          onAddToCart={addPersonalizedToCart}
          recommendedIngredients={functionalIngredientUtils.getRecommendedIngredients({})}
        />
      )}

      {/* Cart Notification */}
      <CartNotification
        isVisible={cartNotification.isVisible}
        onClose={hideCartNotification}
        productName={cartNotification.productName}
        isPersonalized={cartNotification.isPersonalized}
        ingredientCount={cartNotification.ingredientCount}
        totalPrice={cartNotification.totalPrice}
      />
    </div>
  );
}